import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Paciente } from '../services/api/pacientesService';

interface PacienteSearchSelectProps {
  pacientes: Paciente[];
  value: string; // pacienteId selecionado
  onChange: (pacienteId: string, paciente: Paciente | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  label?: string;
}

/**
 * Componente unificado de busca e seleção de pacientes
 * Combina input de busca com dropdown de seleção
 */
export default function PacienteSearchSelect({
  pacientes,
  value,
  onChange,
  placeholder = 'Digite para buscar ou selecione um paciente...',
  disabled = false,
  error,
  required = false,
  label = 'Paciente'
}: PacienteSearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Encontrar paciente selecionado
  const pacienteSelecionado = pacientes.find(p => p.id === value);

  // Filtrar pacientes baseado no termo de busca
  const pacientesFiltrados = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return pacientes;
    }
    const termo = searchTerm.toLowerCase();
    return pacientes.filter(p => 
      p.nome.toLowerCase().includes(termo) ||
      (p.email && p.email.toLowerCase().includes(termo)) ||
      (p.telefone && p.telefone.includes(termo))
    );
  }, [pacientes, searchTerm]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Restaurar texto do paciente selecionado se existir
        if (pacienteSelecionado) {
          setSearchTerm(pacienteSelecionado.nome);
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pacienteSelecionado]);

  // Atualizar searchTerm quando valor muda externamente
  useEffect(() => {
    if (pacienteSelecionado) {
      setSearchTerm(pacienteSelecionado.nome);
    } else {
      setSearchTerm('');
    }
  }, [pacienteSelecionado]);

  // Scroll para item destacado
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    
    // Se limpar o campo, limpar seleção
    if (!e.target.value.trim()) {
      onChange('', null);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Selecionar todo o texto para facilitar nova busca
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleSelectPaciente = useCallback((paciente: Paciente) => {
    onChange(paciente.id, paciente);
    setSearchTerm(paciente.nome);
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [onChange]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < pacientesFiltrados.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && pacientesFiltrados[highlightedIndex]) {
          handleSelectPaciente(pacientesFiltrados[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        if (pacienteSelecionado) {
          setSearchTerm(pacienteSelecionado.nome);
        }
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="paciente-search-select" ref={containerRef}>
      {label && (
        <label className="form-label">
          <i className="fas fa-user-check mr-2"></i>
          {label} {required && '*'}
        </label>
      )}
      
      <div className={`input-group ${error ? 'is-invalid' : ''}`}>
        <div className="input-group-prepend">
          <span className="input-group-text">
            <i className="fas fa-search"></i>
          </span>
        </div>
        
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${error ? 'is-invalid' : ''} ${pacienteSelecionado ? 'border-success' : ''}`}
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        
        {value && (
          <div className="input-group-append">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClear}
              title="Limpar seleção"
              disabled={disabled}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        
        <div className="input-group-append">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
          >
            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>
      
      {error && <div className="invalid-feedback d-block">{error}</div>}
      
      {/* Dropdown de resultados */}
      {isOpen && (
        <ul
          ref={listRef}
          className="paciente-search-dropdown list-group shadow-sm"
          role="listbox"
        >
          {pacientesFiltrados.length === 0 ? (
            <li className="list-group-item text-muted text-center py-3">
              <i className="fas fa-search mr-2"></i>
              {searchTerm ? 'Nenhum paciente encontrado' : 'Digite para buscar...'}
            </li>
          ) : (
            pacientesFiltrados.map((paciente, index) => (
              <li
                key={paciente.id}
                className={`list-group-item list-group-item-action ${
                  highlightedIndex === index ? 'active' : ''
                } ${paciente.id === value ? 'bg-light border-success' : ''}`}
                onClick={() => handleSelectPaciente(paciente)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectPaciente(paciente);
                  }
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={paciente.id === value}
                tabIndex={0}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong className={highlightedIndex === index ? 'text-white' : ''}>
                      {paciente.nome}
                    </strong>
                    {paciente.id === value && (
                      <i className="fas fa-check text-success ml-2"></i>
                    )}
                    <div className={`small ${highlightedIndex === index ? 'text-white-50' : 'text-muted'}`}>
                      {paciente.telefone}
                      {paciente.email && ` • ${paciente.email}`}
                    </div>
                  </div>
                  {paciente.servico && (
                    <span className={`badge ${
                      highlightedIndex === index 
                        ? 'badge-light' 
                        : paciente.servico === 'triagem' 
                          ? 'badge-warning' 
                          : 'badge-info'
                    }`}>
                      {paciente.servico === 'triagem' ? 'Triagem' : 'Psicoterapia'}
                    </span>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
      
      {/* Informações do paciente selecionado */}
      {pacienteSelecionado && !isOpen && (
        <div className="mt-2 p-2 bg-light rounded border-left border-success" style={{ borderLeftWidth: '3px' }}>
          <div className="row small">
            <div className="col-auto">
              <i className="fas fa-phone text-muted mr-1"></i>
              {pacienteSelecionado.telefone}
            </div>
            {pacienteSelecionado.email && (
              <div className="col-auto">
                <i className="fas fa-envelope text-muted mr-1"></i>
                {pacienteSelecionado.email}
              </div>
            )}
            {pacienteSelecionado.servico && (
              <div className="col-auto">
                <span className={`badge ${
                  pacienteSelecionado.servico === 'triagem' 
                    ? 'badge-warning' 
                    : 'badge-info'
                }`}>
                  {pacienteSelecionado.servico === 'triagem' ? 'Triagem' : 'Psicoterapia'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .paciente-search-select {
          position: relative;
        }
        
        .paciente-search-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          z-index: 1050;
          margin-top: 2px;
          border: 1px solid rgba(0,0,0,0.15);
          border-radius: 0.25rem;
          background: #fff;
        }
        
        .paciente-search-dropdown .list-group-item {
          cursor: pointer;
          border-left: none;
          border-right: none;
          padding: 0.75rem 1rem;
        }
        
        .paciente-search-dropdown .list-group-item:first-child {
          border-top: none;
          border-radius: 0.25rem 0.25rem 0 0;
        }
        
        .paciente-search-dropdown .list-group-item:last-child {
          border-bottom: none;
          border-radius: 0 0 0.25rem 0.25rem;
        }
        
        .paciente-search-dropdown .list-group-item.active {
          background-color: #007bff;
          border-color: #007bff;
        }
        
        .paciente-search-dropdown .list-group-item:hover:not(.active) {
          background-color: #f8f9fa;
        }
        
        /* Mobile */
        @media (max-width: 767.98px) {
          .paciente-search-dropdown {
            max-height: 250px;
          }
          
          .paciente-search-dropdown .list-group-item {
            padding: 1rem;
          }
        }
        
        /* Dark mode */
        .dark-mode .paciente-search-dropdown {
          background-color: #343a40;
          border-color: #495057;
        }
        
        .dark-mode .paciente-search-dropdown .list-group-item {
          background-color: #343a40;
          border-color: #495057;
          color: #fff;
        }
        
        .dark-mode .paciente-search-dropdown .list-group-item:hover:not(.active) {
          background-color: #495057;
        }
        
        .dark-mode .paciente-search-dropdown .list-group-item.active {
          background-color: #17a2b8;
          border-color: #17a2b8;
        }
      `}</style>
    </div>
  );
}
